import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Truck, CalendarCheck2, LineChart, ArrowUpRight } from "lucide-react";
import { IMAGES } from "@/lib/assets";
import { useSmartScroll } from "@/lib/useSmartScroll";

const ENGINES = [
  {
    id: "flow",
    kicker: "ENGINE.01",
    name: "Flow Engine",
    industries: "Trades · Logistics · Construction · HVAC",
    icon: Truck,
    image: IMAGES.flow,
    bullets: [
      "Inventory + parts tracking across field & shop",
      "Foreman-to-supplier direct price checks",
      "Truck-to-warehouse communication ledger",
      "Job-costing tied to real-time material draw",
    ],
    footnote: "Built for Wired SC — replaces text-chain chaos with a single operational surface.",
  },
  {
    id: "booking",
    kicker: "ENGINE.02",
    name: "Booking Engine",
    industries: "Service · Hospitality · Clinics · Wellness",
    icon: CalendarCheck2,
    image: IMAGES.booking,
    bullets: [
      "Client-matching + dynamic scheduling",
      "Auto route optimization for mobile teams",
      "Automated SMS / email follow-up sequences",
      "No-show recovery + rebooking workflows",
    ],
    footnote: "For businesses whose revenue lives and dies on the calendar.",
  },
  {
    id: "capital",
    kicker: "ENGINE.03",
    name: "Capital Engine",
    industries: "Retail · B2B · Wholesale · Prof. Services",
    icon: LineChart,
    image: IMAGES.capital,
    bullets: [
      "Custom invoicing + contract signing",
      "Branded client portal with document vault",
      "Dynamic pricing calculators + quote builder",
      "Renewal + upsell automation ledger",
    ],
    footnote: "Where money moves — and margin is defended.",
  },
];

const cardVariant = {
  initial: { opacity: 0, y: 30 },
  animate: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.65, delay: i * 0.12, ease: [0.19, 1, 0.22, 1] },
  }),
};

const EnginesMatrix = () => {
  const smart = useSmartScroll();

  return (
    <section id="engines" data-testid="engines-section" className="relative py-28 lg:py-36">
      <div className="w-full mx-auto px-6 md:px-10 lg:px-16 xl:px-24 2xl:px-32">
        <div className="grid lg:grid-cols-12 gap-8 mb-16">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-4 mb-6">
              <div className="h-px w-8 bg-nex-gold" />
              <span className="mono-label">// The Engine Suite</span>
            </div>
            <h2 className="serif-head text-4xl md:text-6xl mb-6">
              Three engines. <span className="italic text-nex-gold">Ninety percent</span> of every small business problem.
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pt-6">
            <p className="text-nex-chrome text-lg leading-relaxed">
              Traditional software companies build everything for everyone. NEX builds three <span className="text-nex-silver">core templates</span>, then re-skins and tunes them to your exact workflow. Modular scalability is why we deploy in weeks — not quarters.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {ENGINES.map((e, i) => {
            const Icon = e.icon;
            return (
              <motion.article
                key={e.id}
                data-testid={`engine-card-${e.id}`}
                variants={cardVariant}
                initial="initial"
                whileInView="animate"
                viewport={{ once: true, margin: "-80px" }}
                custom={i}
                className="card-industrial corner-tick p-7 flex flex-col group"
              >
                <div className="relative -mx-7 -mt-7 mb-6 h-48 overflow-hidden">
                  <img src={e.image} alt={e.name} className="w-full h-full object-cover opacity-60 group-hover:opacity-80 transition-opacity duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A] via-[#0F172A]/60 to-transparent" />
                  <div className="absolute top-4 left-4 flex items-center gap-2">
                    <div className="w-10 h-10 bg-[#0B0F19]/90 border border-nex-gold/60 flex items-center justify-center">
                      <Icon size={18} className="text-nex-gold" />
                    </div>
                    <span className="mono-label">{e.kicker}</span>
                  </div>
                </div>

                <h3 className="serif-head text-3xl mb-2">{e.name}</h3>
                <p className="mono-body text-[11px] uppercase tracking-widest mb-6">{e.industries}</p>

                <ul className="space-y-3 mb-6 flex-1">
                  {e.bullets.map((b, k) => (
                    <li key={k} className="flex items-start gap-3 text-nex-silver text-sm leading-snug">
                      <span className="text-nex-gold mt-1.5 shrink-0 font-mono text-[10px]">▸</span>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>

                <div className="metal-divider mb-4" />
                <p className="mono-body text-[11px] italic mb-4">{e.footnote}</p>

                {e.id === "flow" ? (
                  <Link
                    to="/demo?engine=flow"
                    data-testid={`engine-cta-${e.id}`}
                    className="inline-flex items-center gap-2 text-nex-gold hover:text-nex-goldbright text-sm font-semibold transition-colors"
                  >
                    See Live Demo <ArrowUpRight size={14} />
                  </Link>
                ) : (
                  <Link
                    to={`/demo?engine=${e.id}`}
                    data-testid={`engine-cta-${e.id}`}
                    className="inline-flex items-center gap-2 text-nex-gold hover:text-nex-goldbright text-sm font-semibold transition-colors"
                  >
                    See Live Demo <ArrowUpRight size={14} />
                  </Link>
                )}
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default EnginesMatrix;
